import { Box } from "@bcc-ui/ui-components";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AllReportsTable from "./AllReportsTable";
import style from "./erasure-reports-table.scss";
import ManageEditReportView from "./ManageEditReportView";
import ReportViewKebabMenu from "./views/ReportViewKebabMenu";
import KebabMenu from "components/kebab-menu/KebabMenu";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { ExportDetails } from "components/reports/AllReportsView";
import ExportReportsView from "components/reports/export/ExportReportsView";
import ImportReportsView from "components/reports/import/ImportReportsView";
import { ALL_REPORTS_ROUTE, REPORTS_ROUTE } from "components/router/Routes";
import { AUTH_REPORT_EXPORT_ALL, AUTH_REPORT_IMPORT } from "domain/authority";
import { CustomReportView, Path } from "domain/reports";
import { reportService } from "services/report/erasure/ReportService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";

export interface Props {
    viewDetails: CustomReportView;
}

export default function IssueReportsView(props?: Props): JSX.Element {
    const { t } = useTranslation();
    const [state, setState] = useState({ count: 0 });
    const [refreshingCount, setRefreshingCount] = React.useState(0);
    function onRefreshing() {
        setRefreshingCount((prevState) => prevState + 1);
    }
    const [exportDetails, setExportDetails] = React.useState<ExportDetails | null>(null);
    const [exportVisibility, setExportVisibility] = React.useState(false);
    const [totalReportCount, setTotalReportCount] = React.useState<number>(0);
    const [disableExportReportButton, setDisableExportReportButton] = React.useState(false);
    const [show, setShow] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(true);
    const [editReportUuid, setEditReportUuid] = React.useState<string>("");

    let viewTitle = t("Common.issueReports");
    let viewDetails: CustomReportView = {
        uuid: "",
        name: "",
        columns: ["uuid", "product", "date", Path.ISSUE_COMMENT],
        shared: false,
        own: true,
        filters: {
            filters: [
                {
                    name: "subtype",
                    value: "ISSUE",
                    operator: "EQUAL",
                    pathType: "KEYWORD",
                },
                {
                    name: Path.ISSUE_COMMENT,
                    value: "*",
                    operator: "EXIST",
                    pathType: "KEYWORD",
                    logicOperator: "OR",
                },
            ],
        },
    };
    if (props != undefined && props.viewDetails != undefined) {
        viewTitle = props.viewDetails.name;
        viewDetails = props.viewDetails;
    }

    function onViewDelete() {
        window.location.replace(ALL_REPORTS_ROUTE.path);
    }

    const kebabMenu = viewDetails && (
        <Box className={style.kebabMenuContainer}>
            <KebabMenu>
                <ReportViewKebabMenu
                    name={viewTitle}
                    viewDetails={viewDetails}
                    onViewDelete={onViewDelete}
                    shared={viewDetails.shared}
                    hideEdit={true}
                    hideDelete={true}
                />
            </KebabMenu>
        </Box>
    );

    const exportButton = exportVisibility &&
        userSessionService.userHasAllAuthorities([AUTH_REPORT_EXPORT_ALL]) &&
        !hasTenantCookie() && (
            <ExportReportsView
                isAllReportsView={props?.viewDetails?.uuid == "undefined" || props?.viewDetails?.uuid == ""}
                columns={viewDetails.columns}
                filters={viewDetails.filters}
                startDate={exportDetails?.startDate}
                searchQuery={exportDetails ? exportDetails.searchQuery : ""}
                endDate={exportDetails?.endDate}
                exportAllButton={true}
                totalReportCount={totalReportCount}
                disableExportReportButton={disableExportReportButton}
            />
        );

    const importButton = !hasTenantCookie() && userSessionService.userHasAllAuthorities([AUTH_REPORT_IMPORT]) && (
        <ImportReportsView />
    );

    const createViewActions = () => {
        return (
            <>
                {exportButton}
                {importButton}
            </>
        );
    };

    function onReportDelete() {
        setState((prevState) => ({ count: prevState.count - 1 }));
    }

    function onDetailsChanged(exportDetails: ExportDetails) {
        setExportDetails(exportDetails);
    }
    React.useEffect(() => {
        setLoading(false);
    });

    return (
        <>
            <ApplicationLayout
                viewTitle={viewTitle}
                view={
                    !show ? (
                        <AllReportsTable
                            count={state.count}
                            fetchAllReportsService={reportService}
                            viewDetails={viewDetails}
                            onReportDelete={onReportDelete}
                            onDetailsChanged={onDetailsChanged}
                            refreshCount={refreshingCount}
                            setExportVisibility={setExportVisibility}
                            setTotalReportCount={setTotalReportCount}
                            setDisableExportReportButton={setDisableExportReportButton}
                            showForm={setShow}
                            editReportUuid={setEditReportUuid}
                            hideDialogView={setShow}
                            hasOnlyBmsReports={true}
                            hideEdit={true}
                        />
                    ) : loading ? (
                        <LoadingIndicator isView />
                    ) : (
                        <ManageEditReportView hideDialogView={setShow} reportUuid={editReportUuid} />
                    )
                }
                mainRoute={REPORTS_ROUTE}
                kebabMenu={kebabMenu}
                viewActions={createViewActions()}
                onHandleRefresh={onRefreshing}
                enableRefresh={true}
            />
        </>
    );
}
